/* ============================================================================
   TELEMETRY — tell someone when something fails quietly
   ----------------------------------------------------------------------------
   The failures that hurt are the ones nobody sees. A requester whose draft
   never saved, an email team member who pasted a stale clipboard into Eloqua, a
   preview that stopped responding to clicks. Each of those looks like the tool
   working right up until it doesn't.

   Rules:
     · never block the user — fire and forget, always
     · never throw — a broken reporter must not break the app
     · deduplicate — the same fault in a loop must not flood a channel
     · say what happened, where, and what the person was doing

   The browser posts to our Worker. The Worker holds the webhook URL and
   forwards to Teams. The URL never reaches the browser.
   ========================================================================== */

import { VERSION } from "../version.js";

const ENDPOINT = "/api/report";
const seen = new Map();          /* key -> last sent (ms) */
const WINDOW = 10 * 60 * 1000;   /* same fault, at most once every ten minutes */
const MAX_PER_SESSION = 20;
let sent = 0;

export const LEVELS = {
  /* someone is blocked right now */
  critical: "critical",
  /* output may be wrong and nobody would know */
  silent: "silent",
  /* worth knowing, nobody is stuck */
  warn: "warn",
};

export function report(level, event, detail = {}) {
  try {
    const key = `${level}:${event}:${detail.code || ""}`;
    const now = Date.now();
    if (sent >= MAX_PER_SESSION) return;
    if (seen.has(key) && now - seen.get(key) < WINDOW) return;
    seen.set(key, now);
    sent++;

    const body = JSON.stringify({
      level,
      event,
      detail,
      version: VERSION,
      at: new Date().toISOString(),
      url: typeof location !== "undefined" ? location.pathname : "",
      agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    });

    /* sendBeacon survives the page closing, which is exactly when a crash
       report is most likely to be lost. */
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
    fetch(ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true })
      .catch(() => {});
  } catch (e) { /* a reporter that throws is worse than no reporter */ }
}

/* Named events, so the Teams channel reads as sentences rather than codes. */
export const EVENTS = {
  APP_CRASHED:        "The composer crashed",
  DRAFT_SAVE_FAILED:  "A draft could not be saved at all",
  DRAFT_SAVE_DEGRADED:"A draft saved without its pictures",
  DRAFT_CORRUPT:      "A saved draft could not be read",
  STORAGE_BLOCKED:    "This browser refuses to store drafts",
  COPY_FAILED:        "Copy to clipboard failed",
  PREVIEW_DEAD:       "The preview stopped responding to clicks",
  BLOCKS_DROPPED:     "A restored draft lost blocks the library no longer has",
  IMPORT_POOR:        "An import matched very little of the source email",
  IMAGE_FAILED:       "An image could not be processed",
  ELOQUA_SYNC_FAILED: "Eloqua rejected a sync",       /* Phase 6 */
  ELOQUA_UPLOAD_FAILED: "Eloqua rejected an image",   /* Phase 3 */
  PREFLIGHT_FAILED:   "Generated HTML failed a structural check",
};
